import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Check, Calendar, AlertCircle } from 'lucide-react';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MONTH_ABBR = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

export function MonthCard({ month, year, postCount = 0, index, needsReview = 0, basePath = '/month' }) {
  const navigate = useNavigate();
  const monthName = MONTH_NAMES[month - 1];
  const monthAbbr = MONTH_ABBR[month - 1];
  
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const isPast = year < currentYear || (year === currentYear && month < currentMonth);
  const isCurrent = year === currentYear && month === currentMonth;

  const handleClick = () => {
    navigate(`${basePath}/${year}/${month}`);
  };

  let cardClasses = 'w-full rounded-2xl p-5 text-left opacity-0 animate-slide-up relative overflow-hidden transition-all duration-300 ';
  
  if (isPast) {
    cardClasses += 'border';
  } else if (isCurrent) {
    cardClasses += 'shadow-2xl';
  } else {
    cardClasses += 'bg-white border border-[#e2e8f0] hover:shadow-xl';
  }

  const pastMonthStyle = isPast ? {
    backgroundColor: 'color-mix(in srgb, var(--brand-primary) 6%, #f0f0f0)',
    borderColor: 'color-mix(in srgb, var(--brand-primary) 10%, #ddd)',
  } : {};

  const currentMonthStyle = isCurrent ? {
    background: `linear-gradient(to bottom right, var(--brand-primary), var(--brand-accent))`,
    boxShadow: `0 25px 50px -12px color-mix(in srgb, var(--brand-primary) 40%, transparent)`
  } : {};

  return (
    <button
      onClick={handleClick}
      className={`${cardClasses} stagger-${index + 1}`}
      style={{...pastMonthStyle, ...currentMonthStyle}}
      data-testid={`month-card-${monthName.toLowerCase()}`}
    >
      {/* Decorative element for current month */}
      {isCurrent && (
        <>
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        </>
      )}

      {/* Needs Attention Badge */}
      {needsReview > 0 && !isPast && (
        <div className="absolute top-3 right-3 z-10" data-testid={`attention-badge-${monthName.toLowerCase()}`}>
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold ${
            isCurrent ? 'bg-amber-400 text-amber-900' : 'bg-amber-100 text-amber-700'
          }`}>
            <AlertCircle className="w-3 h-3" />
            {needsReview}
          </div>
        </div>
      )}
      
      <div className="relative">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className={`text-xs font-bold tracking-[0.2em] mb-1 ${
              isPast ? 'text-[#94a3b8]' : isCurrent ? 'text-white/80' : ''
            }`}
              style={!isPast && !isCurrent ? { color: 'var(--brand-accent)' } : {}}
            >
              {monthAbbr}
            </div>
            <h3 className={`text-xl font-bold tracking-tight ${
              isPast ? 'text-[#64748b]' : isCurrent ? 'text-white' : 'text-[#1e293b]'
            }`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {monthName}
            </h3>
          </div>
          {needsReview > 0 ? null : isPast ? (
            <div className="w-8 h-8 rounded-full bg-[#cbd5e1] flex items-center justify-center">
              <Check className="w-4 h-4 text-[#64748b]" />
            </div>
          ) : isCurrent ? (
            <div className="w-8 h-8 rounded-full bg-white/25 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-white" />
            </div>
          ) : (
            <ChevronRight className="w-5 h-5 text-[#cbd5e1]" />
          )}
        </div>

        <div className="flex items-end justify-between mt-4">
          <div className="flex items-baseline gap-1">
            <span className={`text-3xl font-bold ${
              isPast ? 'text-[#94a3b8]' : isCurrent ? 'text-white' : 'text-[#1e293b]'
            }`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {postCount}
            </span>
            <span className={`text-sm ${
              isPast ? 'text-[#94a3b8]' : isCurrent ? 'text-white/70' : 'text-[#64748b]'
            }`}>
              posts
            </span>
          </div>
          {postCount > 0 && (
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
              isPast 
                ? 'text-[#64748b] bg-[#cbd5e1]' 
                : isCurrent
                  ? 'bg-white font-bold'
                  : ''
            }`}
              style={isCurrent 
                ? { color: 'var(--brand-primary)' }
                : !isPast 
                  ? { color: 'var(--brand-accent)', backgroundColor: 'color-mix(in srgb, var(--brand-accent) 10%, transparent)' }
                  : {}
              }
            >
              {isPast ? 'Done' : isCurrent ? 'Active' : 'Planned'}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
